import { usePersonReadDataContext } from '@aibees/elephant/context';
import { usePersonUpdatesStore } from '@aibees/elephant/store/person-updates';
import { CreditUsageType } from '@aibees/shared/definition';
import { useBoolean } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { LoadingButton } from '@mui/lab';
import { memo, useCallback, useMemo, useRef } from 'react';
import { CreditRuleCoin } from '../credit-rule-coin';
import {
  doesAiAnalysisHasAnyUpdates,
  isAiAnalysisUnlocked,
} from '../person-ai-analysis-info/utils';
import { useHandleEnrichPersonalityAnalysis } from '../quick-info-group/hooks/use-handle-enrich-personality-analysis';
import * as Styled from './styles';
import * as SX from './sx';
import type { UnlockAiAnalysisProps as Props } from './types';

export const UnlockAiAnalysis = memo(({ className }: Props) => {
  const hover = useBoolean(false);
  const { api } = usePersonReadDataContext();
  const unlocked = isAiAnalysisUnlocked(api?.data);
  const hasUpdates = doesAiAnalysisHasAnyUpdates(api?.data);
  const { setAnalysisUpdateTriggered } = usePersonUpdatesStore();

  const { handleAnalysis, loading, failed } =
    useHandleEnrichPersonalityAnalysis(api?.data);

  const handleUnlock = useCallback(() => {
    handleAnalysis();
    hover.onFalse();
  }, [handleAnalysis, hover]);

  const handleUpdate = useCallback(() => {
    setAnalysisUpdateTriggered(true);
    handleAnalysis(); // TODO: whenever back-end provides the update API this will be replaced!
    hover.onFalse();
  }, [setAnalysisUpdateTriggered, handleAnalysis, hover]);

  const action = useMemo(() => {
    if (failed) return { label: 'Retry', handler: handleUnlock };
    if (hasUpdates) return { label: 'Update', handler: handleUpdate };
    return null;
  }, [failed, hasUpdates, handleUnlock, handleUpdate]);

  const handleClick = () => {
    action?.handler();
  };

  // Preserve last action's label during loading so the button keeps its
  // natural width (MUI hides the text via `color: transparent` but the width
  // is still occupied) and we avoid a layout jump on the way out.
  const lastActionRef = useRef(action);
  if (action) lastActionRef.current = action;
  const displayLabel = action?.label ?? lastActionRef.current?.label;

  if (!unlocked) return null;

  return (
    <Styled.Wrapper
      className={classnames('UnlockAiAnalysis', className)}
      sx={SX.root}
    >
      {hover.value && !loading && (
        <CreditRuleCoin
          type={CreditUsageType.CONTACT_ANALYSIS_UPDATE}
          sx={SX.creditTag}
        />
      )}
      {(action || loading) && (
        <LoadingButton
          variant="contained"
          color="primary"
          size="xsmall"
          loading={loading}
          onClick={handleClick}
          onMouseEnter={hover.onTrue}
          onMouseLeave={hover.onFalse}
        >
          {displayLabel}
        </LoadingButton>
      )}
    </Styled.Wrapper>
  );
});

UnlockAiAnalysis.displayName = 'UnlockAiAnalysis';
